import { computed, inject, Injectable, signal } from "@angular/core";
import { takeUntilDestroyed } from "@angular/core/rxjs-interop";
import { GenAiService } from "@shared-api/genAi/genAi.service";
import { ProcessService } from "@shared-api/process/process.service";
import { WorkflowService } from "@shared-api/workflow/workflow.service";
import { ContentCollectionService } from "@shared-api/content-collection/content-collection.service";
// import { FileData } from "@shared-interfaces/shared-file";
import { BasicProcessRepsonse, ProcessResponse, PromptResponse } from "@shared-interfaces/shared-process";
import {
    AnalysisProgress,
    AnalysisResult,
    ColorCount,
    ContractAnalysisResult,
    DoraResponse,
    FilterState,
    NewContractAnalysisResult,
    Workflow
} from "@shared-interfaces/shared-workflow";
import { LoaderService } from "@shared-utils/loader/loader.service";
import { catchError, EMPTY, filter, of, Subject, switchMap, take, tap } from "rxjs";
import { allSameValue, atLeastOneValue } from "@shared-helper/helper-functions";
import { FileData } from "@shared-types/shared-file";
import { MatSnackBar } from "@angular/material/snack-bar";
import { TranslateService } from '@ngx-translate/core';
import { classifySummaryAnswer, countQuestionVerdicts } from '../../contract/classify-summary-answer';

export type WorkflowStateStatus = "loading" | "success" | "error";

interface WorkflowState {
    status: WorkflowStateStatus;
    workflowData: Workflow | undefined;
}

@Injectable({
    providedIn: "root",
})
export class WorkflowOverviewService {
    private workflowService = inject(WorkflowService);
    private genaiService = inject(GenAiService);
    private loaderService = inject(LoaderService);
    private processService = inject(ProcessService)
    private translateService = inject(TranslateService);
    private contentCollectionService = inject(ContentCollectionService);

    // state
    public componentState = signal<WorkflowState>({
        status: "loading",
        workflowData: undefined,
    });

    public filters = signal<FilterState>({
        yes: false,
        no: false,
        check: false
    })

    public analysisProgress = signal<AnalysisProgress | null>(null);

    private pollingIntervalId: ReturnType<typeof setInterval> | null = null;

    // selectors
    status = computed(() => this.componentState().status);
    workflowData = computed(() => this.componentState().workflowData);
    workflowId = computed(() => this.componentState().workflowData?.uid);
    fileList = computed(() => this.componentState().workflowData?.files);
    runningProcesses = computed(() => this.componentState().workflowData?.runningProcesses);
    specifications = computed(() => this.componentState().workflowData?.specifications)

    contractResults = computed<AnalysisResult[]>(() => {
        // Use optional chaining and nullish coalescing to safely handle undefined
        const contractResultsData = this.componentState().workflowData?.specifications?.analysisResult;

        if (!contractResultsData) {
            return [];
        }

        // Check if it's the new array format
        if (Array.isArray(contractResultsData)) {
            const results = contractResultsData.map((item, index) => {
                const result: AnalysisResult = {
                    question: item.promptBasic?.name || `Question ${index + 1}`,
                    files: item.analysisResultPerFile,
                    key: index,
                    promptBasic: item.promptBasic,
                    analysisSummary: item.analysisSummary
                };
                return result;
            }).sort((a, b) => {
                // Sort by rank if available, otherwise fall back to key sorting
                const rankA = a.promptBasic?.rank ?? a.key;
                const rankB = b.promptBasic?.rank ?? b.key;
                return rankA - rankB;
            });

            return results;
        } else {
            // Handle old format for backward compatibility
            if (Object.keys(contractResultsData).length > 0) {
                const questionsLength = Object.entries(contractResultsData).length;
                const getQuestions = () => {
                    const mapping = questionsLength < 52
                        ? this.translateService.instant('titleMapping15')
                        : this.translateService.instant('titleMapping52');
                    return mapping;
                }

                const keys = Object.keys(contractResultsData);

                const results = keys.map(item => {
                    const v: DoraResponse[] = (contractResultsData as any)[item];

                    const questionKey = item;
                    const questionMapping = getQuestions();
                    // @ts-ignore
                    const questionText = questionMapping[item];
                    const keyNumber = Number(item.substring(8));
                    const promptBasic = v[0]?.promptBasic;
                    const result = {
                        // @ts-ignore
                        question: questionText,
                        files: v,
                        key: keyNumber,
                        promptBasic
                    };
                    return result;
                }).sort((a, b) => {
                    // Sort by rank if available, otherwise fall back to key sorting
                    const rankA = a.promptBasic?.rank ?? a.key;
                    const rankB = b.promptBasic?.rank ?? b.key;
                    return rankA - rankB;
                });

                return results;
            }
        }

        return [];
    });

    contractFilteredResults = computed<AnalysisResult[]>(() => {
        return this.contractResults()
            .filter(item => {
                if (!this.filters().yes && !this.filters().no && !this.filters().check) {
                    return true;
                } else {
                    const summaryAnswer = item.analysisSummary?.shortAnswer;
                    let isYes: boolean;
                    let isNo: boolean;
                    if (summaryAnswer) {
                        // Word-boundary classification of the free-form summary sentence.
                        const verdict = classifySummaryAnswer(summaryAnswer);
                        isYes = verdict === 'yes';
                        isNo = verdict === 'no';
                    } else {
                        isYes = atLeastOneValue('Ja', item.files) || atLeastOneValue('Yes', item.files);
                        isNo = allSameValue('Nein', item.files) || allSameValue('No', item.files);
                    }
                    const isCheck = !isYes && !isNo;

                    if (this.filters().yes && isYes) return true;
                    if (this.filters().no && isNo) return true;
                    if (this.filters().check && isCheck) return true;
                    return false;
                }
            })
    });
    promptContext = computed(() => this.componentState().workflowData?.promptContext);
    prompt = computed(() => this.componentState().workflowData?.userPrompt);
    promptResults = computed(() => this.componentState().workflowData?.promptResults);
    shortAnswerCounts = computed((): ColorCount => {
        // Shared with the dashboard tile's activeStep number (see buildActiveStepNumber)
        // so the overview card and this page can never disagree.
        const { yes, no, tbc } = countQuestionVerdicts(this.contractResults());
        return { yes, no, zuprufen: tbc };
    });
    workflowName = computed(() => this.componentState().workflowData?.name)

    // Detect if using old schema format
    isUsingOldSchema = computed(() => {
        const contractResultsData = this.componentState().workflowData?.specifications?.analysisResult;
        // If contractResultsData exists and is not an array, it's the old schema
        return contractResultsData && !Array.isArray(contractResultsData) && Object.keys(contractResultsData).length > 0;
    });

    // Actions
    private error$ = new Subject<any>();

    getWorkflowData$ = new Subject<string | undefined>();
    workflowData$ = this.getWorkflowData$.pipe(
        switchMap((workflowId: string | undefined) => {
            if (workflowId) {
                // Reset stale progress and stop any ongoing poll from a previous contract.
                this.stopPolling();
                this.analysisProgress.set(null);
                this.updateStatus("loading");
                return this.getWorkflowData(workflowId).pipe(
                    switchMap((workflowData) => {
                        // step=3 means analysis completed; results are stored in a Firestore
                        // subcollection to avoid document size limits. Fetch them separately.
                        // Always attempt to fetch analysis results on load.
                        // get_analysis_result is the source of truth — it returns results
                        // regardless of whether step===3 or storedInSubcollection is set,
                        // covering both old and new polling-based analysis flows.
                        if (workflowData?.uid) {
                            return this.genaiService.getAnalysisResult(workflowData.uid).pipe(
                                switchMap((analysisResult) => {
                                    // Restore progress signal on refresh
                                    if (analysisResult.progress) {
                                        this.analysisProgress.set(analysisResult.progress);
                                        // Resume polling if the job is still running
                                        if (analysisResult.progress.status === 'in_progress' || analysisResult.progress.status === 'not_started') {
                                            this.startProgressPolling(workflowData.uid!);
                                        }
                                    }

                                    // Only merge results if the response actually contains analysis data
                                    const hasResults = analysisResult.analysisResult &&
                                        (Array.isArray(analysisResult.analysisResult)
                                            ? (analysisResult.analysisResult as any[]).length > 0
                                            : Object.keys(analysisResult.analysisResult).length > 0);

                                    if (!hasResults) {
                                        return of(workflowData);
                                    }

                                    return of({
                                        ...workflowData,
                                        specifications: {
                                            ...workflowData.specifications,
                                            analysisResult: analysisResult.analysisResult,
                                            promptSetId: analysisResult.promptSetId,
                                            numberOfQuestions: analysisResult.numberOfQuestions,
                                            completedat: analysisResult.completedat,
                                            promptIdentifiers: analysisResult.promptIdentifiers,
                                            storedInSubcollection: true,
                                        }
                                    });
                                }),
                                catchError((err) => {
                                    // 400/404 = no analysis yet for this collection — not an error, just return workflow as-is
                                    return of(workflowData);
                                })
                            );
                        }
                        return of(workflowData);
                    }),
                    catchError((err) => {
                        console.error(err);
                        this.error$.next(err);
                        return EMPTY;
                    })
                );
            } else {
                return EMPTY;
            }
        })
    );

    updateWorkflowData$ = new Subject<void>();
    storeWorkflowData$ = this.updateWorkflowData$.pipe(
        switchMap(() => {
            this.updateStatus("loading");
            return this.storeWorkflowData().pipe(
                catchError((err) => {
                    console.error(err);
                    this.error$.next(err);
                    return EMPTY;
                })
            );
        })
    );

    analyseContract$ = new Subject<[string, 'resume' | 'full']>();
    contractData$ = this.analyseContract$.pipe(
        switchMap(([analysisType, mode]: [string, 'resume' | 'full']) => {
            this.updateStatus("loading");
            this.analysisProgress.set(null);
            this.stopPolling();
            return this.analyseFileData(this.workflowId()!, analysisType, mode).pipe(
                catchError((err) => {
                    console.error(err);
                    this.error$.next(err);
                    return EMPTY;
                })
            );
        })
    );

    testPrompt$ = new Subject<[string, string]>();
    testPromptData$ = this.testPrompt$.pipe(
        switchMap(([prompt, context]) => {
            this.updateStatus("loading");
            return this.testPrompt(prompt, context, this.workflowId()).pipe(
                catchError((err) => {
                    console.error(err);
                    this.error$.next(err);
                    return EMPTY;
                })
            );
        })
    );

    updateFile$ = new Subject<ProcessResponse>();
    updatePromptResults$ = new Subject<ProcessResponse>();
    updatePromptInformation$ = new Subject<[string, string]>();
    addFileData$ = new Subject<FileData>();
    updateContractAnalysis$ = new Subject<ProcessResponse>();
    deleteFileData$ = new Subject<FileData>();

    constructor(private snackBar: MatSnackBar) {
        this.error$
            .pipe(takeUntilDestroyed())
            .subscribe(() => this.updateStatus("error"));

        this.workflowData$.pipe(takeUntilDestroyed()).subscribe({
            next: (workflowData: Workflow | undefined) => {
                let analysisResult = [];
                if (workflowData?.specifications) {

                }
                this.componentState.update((componentState) => ({
                    ...componentState,
                    status: "success",
                    workflowData: {
                        ...workflowData!,
                        contractResults: workflowData?.specifications?.analysisResult ?? workflowData?.contractResults,
                        files: workflowData?.files.map(file => {
                            return {
                                ...file,
                                displayName: `${file.displayName}`
                            }
                        }) || []
                    },
                }));
            },
        });

        this.addFileData$.pipe(takeUntilDestroyed()).subscribe({
            next: (fileData: FileData) => {
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData) {

                        if (!Array.isArray(workflowData.files)) {
                            workflowData.files = [];
                        }

                        workflowData.files.push(fileData);
                    }
                    return {
                        ...componentState,
                        status: "success",
                        workflowData: workflowData,
                    };
                });
            },
        });

        this.contractData$.pipe(takeUntilDestroyed()).subscribe({
            next: (process: BasicProcessRepsonse) => {
                // analyse_contract_v2 responded — the job is now queued on the backend.
                // Start the 2s progress polling loop now that there is an active job.
                const workflowIdForPolling = this.workflowId();
                if (workflowIdForPolling) {
                    this.startProgressPolling(workflowIdForPolling);
                }
                this.processService.startPolling$.next(process);
                return this.processService.processResults$.pipe(filter(result => result.uid === process.uid),
                    take(1)).subscribe((processResults: ProcessResponse) => {
                        if (processResults.status === 'completed') {
                            this.updateStatus("success");
                            const workflowId = this.workflowId()!;
                            this.getWorkflowData(workflowId).pipe(
                                take(1),
                                switchMap((workflowData) => {
                                    const analysisCompleted = workflowData?.step === 3
                                        || workflowData?.specifications?.storedInSubcollection;
                                    if (analysisCompleted && workflowData?.uid) {
                                        return this.genaiService.getAnalysisResult(workflowData.uid).pipe(
                                            switchMap((analysisResult) => of({
                                                ...workflowData,
                                                specifications: {
                                                    ...workflowData.specifications!,
                                                    analysisResult: analysisResult.analysisResult,
                                                    promptSetId: analysisResult.promptSetId,
                                                    numberOfQuestions: analysisResult.numberOfQuestions,
                                                    completedat: analysisResult.completedat,
                                                    promptIdentifiers: analysisResult.promptIdentifiers,
                                                    storedInSubcollection: true,
                                                }
                                            })),
                                            catchError((err) => {
                                                console.error('Error fetching analysis result from subcollection:', err);
                                                return of(workflowData);
                                            })
                                        );
                                    }
                                    return of(workflowData);
                                })
                            ).subscribe(workflowData => {
                                this.componentState.update((state: WorkflowState) => ({
                                    ...state,
                                    status: "success",
                                    workflowData: {
                                        ...workflowData!,
                                        files: workflowData?.files.map(file => ({
                                            ...file,
                                            displayName: `${file.displayName}`
                                        })) || []
                                    }
                                }));
                            });
                        }
                    });
            }
        });

        this.testPromptData$.pipe(takeUntilDestroyed()).subscribe({
            next: (process: PromptResponse) => {
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData && workflowData.specifications) {
                        workflowData.specifications.promptResults = process.promptResults;
                    }
                    return {
                        ...componentState,
                        status: "success",
                        workflowData: workflowData,
                    };
                });
            },
        });

        this.deleteFileData$.pipe(takeUntilDestroyed()).subscribe({
            next: (fileData: FileData) => {
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData) {
                        workflowData.files = workflowData.files.filter(
                            (el) => el.storedName != fileData.storedName
                        );
                    }
                    return {
                        ...componentState,
                        status: "success",
                        workflowData: workflowData,
                    };
                });
            },
        });

        this.updateFile$.pipe(takeUntilDestroyed()).subscribe({
            next: (process: ProcessResponse) => {
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData) {
                        const workflowFiles = workflowData.files.map((el) => {
                            if (el.storedName == process.details.fileInformation) {
                                el.status = process.status
                            }
                            return el
                        }
                        );

                        workflowData = {
                            ...workflowData,
                            files: workflowFiles
                        }
                    }
                    if (workflowData) {
                        workflowData.runningProcesses = workflowData.runningProcesses.filter(
                            (el) => el.uid != process.uid
                        );
                    }
                    return {
                        ...componentState,
                        status: "success",
                        workflowData: workflowData,
                    };
                });
            }
        });

        this.updateContractAnalysis$.pipe(takeUntilDestroyed()).subscribe({
            next: (process: ProcessResponse) => {
                // Remove the completed process from runningProcesses immediately.
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData) {
                        workflowData = {
                            ...workflowData,
                            runningProcesses: workflowData.runningProcesses.filter(
                                (el) => el.uid != process.uid
                            ),
                        };
                    }
                    return { ...componentState, workflowData };
                });

                const workflowId = this.workflowId();
                if (workflowId) {
                    // Fetch full results from subcollection, then fall back to process.result
                    // for backward compatibility with old workflows.
                    this.genaiService.getAnalysisResult(workflowId).pipe(
                        take(1),
                        catchError(() => of(null))
                    ).subscribe((analysisResult) => {
                        this.componentState.update((componentState: WorkflowState) => {
                            let workflowData = componentState.workflowData;
                            if (workflowData) {
                                if (analysisResult) {
                                    workflowData = {
                                        ...workflowData,
                                        specifications: {
                                            ...workflowData.specifications!,
                                            analysisResult: analysisResult.analysisResult,
                                            promptSetId: analysisResult.promptSetId,
                                            numberOfQuestions: analysisResult.numberOfQuestions,
                                            completedat: analysisResult.completedat,
                                            promptIdentifiers: analysisResult.promptIdentifiers,
                                            storedInSubcollection: true,
                                        },
                                        contractResults: analysisResult.analysisResult,
                                        step: 3,
                                    };
                                } else {
                                    workflowData = {
                                        ...workflowData,
                                        contractResults: process.result,
                                    };
                                }
                            }
                            return { ...componentState, status: "success", workflowData };
                        });
                    });
                } else {
                    // No workflow id — fall back to inline result
                    this.componentState.update((componentState: WorkflowState) => {
                        let workflowData = componentState.workflowData;
                        if (workflowData) {
                            workflowData = { ...workflowData, contractResults: process.result };
                        }
                        return { ...componentState, status: "success", workflowData };
                    });
                }
            }
        });

        this.updatePromptResults$.pipe(takeUntilDestroyed()).subscribe({
            next: (process: ProcessResponse) => {
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData) {
                        workflowData.runningProcesses = workflowData.runningProcesses.filter(
                            (el) => el.uid != process.uid
                        );
                    }
                    if (workflowData) {
                        workflowData.promptResults = process.result
                    }
                    return {
                        ...componentState,
                        status: "success",
                        workflowData: workflowData,
                    };
                });
            }
        });

        this.updatePromptInformation$.pipe(takeUntilDestroyed()).subscribe({
            next: ([prompt, promptContext]) => {
                this.componentState.update((componentState: WorkflowState) => {
                    let workflowData = componentState.workflowData;
                    if (workflowData) {
                        workflowData.userPrompt = prompt
                        workflowData.promptContext = promptContext
                    }
                    return {
                        ...componentState,
                        workflowData: workflowData,
                    };
                });
            }
        });
    }

    public updateContractName(name: string): void {
        this.componentState.update((componentState) => ({
            ...componentState,
            workflowData: { ...componentState.workflowData as any, name: name }
        }));
    }


    private getWorkflowData(workflowId: string) {
        return this.workflowService.getWorkflow(workflowId);
    }

    private storeWorkflowData() {
        return this.workflowService.storeWorkflow(
            this.workflowData()?.uid,
            this.workflowData()
        );
    }

    private updateStatus(status: WorkflowStateStatus) {
        this.componentState.update((componentState) => ({
            ...componentState,
            status: status,
        }));
    }

    private analyseFileData(workflowId: string, analysisType: string, mode: 'resume' | 'full' = 'resume') {
        return this.genaiService.analyseContractV2(workflowId, analysisType, mode)
    }

    private startProgressPolling(workflowId: string): void {
        this.stopPolling();
        // Track how many completed prompts were reflected in the last get_analysis_result call.
        // Whenever completedPrompts from the lightweight poll exceeds this, new results are
        // available and we fetch them. Starts at the current result count so we don't
        // re-fetch on the very first tick if results were already loaded on page init.
        let lastFetchedAt = this.contractResults().length;

        this.pollingIntervalId = setInterval(() => {
            this.contentCollectionService.getContentCollection(workflowId).pipe(
                take(1),
                catchError(() => of(null))
            ).subscribe((collection) => {
                if (!collection) return;

                const progress = collection.specifications?.['progress'] ?? null;
                this.analysisProgress.set(progress);

                const completed = progress?.completedPrompts ?? 0;

                if (progress?.status === 'completed') {
                    this.stopPolling();
                    // Always fetch once on completion to get the final full result set.
                    this.fetchAndApplyAnalysisResult(workflowId).subscribe();
                } else if (progress?.status === 'failed') {
                    this.stopPolling();
                    this.updateStatus('error');
                } else if (completed > lastFetchedAt) {
                    // New results are available — fetch and show them, then update the cursor.
                    lastFetchedAt = completed;
                    this.fetchAndApplyAnalysisResult(workflowId).subscribe();
                }
            });
        }, 8000);
    }

    public stopProgressPolling(): void {
        this.stopPolling();
        this.analysisProgress.set(null);
    }

    private stopPolling(): void {
        if (this.pollingIntervalId !== null) {
            clearInterval(this.pollingIntervalId);
            this.pollingIntervalId = null;
        }
    }

    private testPrompt(prompt: string, context: string, workflowId?: string) {
        return this.genaiService.testPrompt(prompt, context, workflowId);
    }

    /**
     * Fetch the latest analysis result from the backend and apply it to the state.
     * Used by the "Load completed results" button during in-progress analysis.
     */
    public fetchAndApplyAnalysisResult(workflowId: string) {
        return this.genaiService.getAnalysisResult(workflowId).pipe(
            take(1),
            tap((result) => {
                if (!result) return;
                this.componentState.update((state) => {
                    const workflowData = state.workflowData;
                    if (!workflowData) return state;
                    return {
                        ...state,
                        workflowData: {
                            ...workflowData,
                            specifications: {
                                ...workflowData.specifications!,
                                analysisResult: result.analysisResult,
                                promptSetId: result.promptSetId,
                                numberOfQuestions: result.numberOfQuestions,
                                completedat: result.completedat,
                                promptIdentifiers: result.promptIdentifiers,
                                storedInSubcollection: true,
                            },
                            contractResults: result.analysisResult,
                        }
                    };
                });
            }),
            catchError((err) => {
                console.error('Failed to load partial results:', err);
                return of(null);
            })
        );
    }

    public resetData() {
        this.componentState.update((componentState) => ({
            status: "loading",
            workflowData: undefined,
        }));
    }

    ngOnDestroy(): void {
        this.stopPolling();
    }

}
